import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { ExhibitionArchCard } from "../ExhibitionArchCard";

const isReduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const RecentShows = () => {
  const [shows, setShows] = useState([]);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const offset = useRef(0);
  const paused = useRef(false);
  const dragging = useRef(false);
  const moved = useRef(false);
  const dragStart = useRef({ x: 0, offset: 0 });
  const velocity = useRef(0);
  const lastScrollY = useRef(0);

  useEffect(() => {
    api
      .get("/exhibitions")
      .then((res) => setShows((res.data || []).filter((e) => e.show_in_recent).slice(0, 6)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!shows.length) return undefined;
    const track = trackRef.current;
    const reduce = isReduced();
    let raf;
    const step = () => {
      const half = track.scrollWidth / 2;
      if (!reduce && !dragging.current && !paused.current) {
        offset.current -= 0.45 + Math.min(Math.abs(velocity.current) * 0.05, 1.4);
      }
      velocity.current *= 0.9;
      if (half > 0) {
        if (offset.current <= -half) offset.current += half;
        if (offset.current > 0) offset.current -= half;
      }
      track.style.transform = `translate3d(${offset.current}px, 0, 0)`;
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    lastScrollY.current = window.scrollY;
    const onScroll = () => {
      velocity.current = window.scrollY - lastScrollY.current;
      lastScrollY.current = window.scrollY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, [shows]);

  const cardStep = () => {
    const track = trackRef.current;
    if (!track || !track.children.length) return 320;
    return track.children[0].getBoundingClientRect().width + 32;
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    moved.current = false;
    dragStart.current = { x: e.clientX, offset: offset.current };
    viewportRef.current.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    const dx = e.clientX - dragStart.current.x;
    if (Math.abs(dx) > 6) moved.current = true;
    offset.current = dragStart.current.offset + dx;
  };
  const endDrag = () => {
    dragging.current = false;
  };

  if (!shows.length) return null;

  return (
    <section className="hairline-t hairline-b" data-testid="recent-shows-section" aria-label="Recent shows">
      <div className="flex items-center justify-between px-5 py-6 lg:px-10">
        <h2 className="h-condensed text-xl lg:text-2xl">Recent Shows</h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-testid="recent-shows-prev"
            aria-label="Previous shows"
            onClick={() => {
              offset.current += cardStep();
            }}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-charcoal transition-colors duration-300 hover:bg-stone-40"
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 2 4 8 10 14" />
            </svg>
          </button>
          <button
            type="button"
            data-testid="recent-shows-next"
            aria-label="Next shows"
            onClick={() => {
              offset.current -= cardStep();
            }}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-charcoal transition-colors duration-300 hover:bg-stone-40"
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="6 2 12 8 6 14" />
            </svg>
          </button>
        </div>
      </div>
      <div
        ref={viewportRef}
        data-testid="recent-shows-viewport"
        className="touch-pan-y select-none overflow-hidden"
        style={{ cursor: "grab" }}
        onMouseEnter={() => {
          paused.current = true;
        }}
        onMouseLeave={() => {
          paused.current = false;
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={(e) => {
          if (moved.current) {
            e.preventDefault();
            e.stopPropagation();
            moved.current = false;
          }
        }}
      >
        <div ref={trackRef} className="flex gap-8 px-5 pb-12 will-change-transform lg:px-10">
          {[...shows, ...shows].map((ex, i) => (
            <ExhibitionArchCard
              key={`${ex.slug}-${i}`}
              exhibition={ex}
              className="w-[83vw] shrink-0 sm:w-[calc(50%-1rem)] lg:w-[calc(33.3333%-1.334rem)]"
            />
          ))}
        </div>
      </div>
    </section>
  );
};
